<template>
  <table class="skill-table">
    <thead>
      <tr>
        <th class="col-name">Skill</th>
        <th class="col-type">Type</th>
        <th class="col-num" title="Resolved impact per target, at this archetype's stats">Impact</th>
        <th class="col-num" title="Targets credited to the pattern, and charges">Spread</th>
        <th class="col-num" title="Cells this skill can reach">Range</th>
        <th class="col-num" title="Action points, plus the walk into range">AP</th>
        <th class="col-num" title="Energy the cast spends">Energy</th>
        <th class="col-num" title="How many times a turn of action points affords it">Casts</th>
        <th class="col-num is-key" title="Power per action point — the number a balance pass reads">Per AP</th>
        <th class="col-num" title="Everything this skill does in one turn's action points">Per turn</th>
      </tr>
    </thead>

    <!-- One table rather than one per school, so the header can stay stuck to
         the top of a long list. The school is a spanning row inside it. -->
    <tbody v-for="group in groups" :key="group.school">
      <tr class="skill-school">
        <th colspan="10">
          <span class="skill-school__name">{{ group.school }}</span>
          <span class="skill-school__count">{{ group.skills.length }} castable</span>
          <span class="skill-school__best">best {{ round(group.bestPerAp) }} per AP</span>
        </th>
      </tr>

      <template v-for="skill in group.skills" :key="skill.skill_id">
        <tr
          class="skill-row"
          :class="{ 'is-open': expanded === skill.skill_id, 'is-off-curve': offCurve(skill) }"
          @click="toggle(skill.skill_id)"
        >
          <td class="col-name">
            <span class="skill-row__name">{{ skill.name }}</span>
            <span class="skill-row__grade">G{{ skill.grade }}</span>
            <span v-if="offCurve(skill)" class="skill-row__flag" :title="skill.curve.findings.join('\n')">!</span>
          </td>
          <td class="col-type">
            <span class="skill-row__type" :class="`type-${skill.skill_type}`">
              {{ typeGlyph(skill.skill_type) }} {{ skill.skill_type }}
            </span>
          </td>
          <td class="col-num">{{ round(skill.coefficients.magnitude) }}<span
            v-if="skill.coefficients.effect_value"
            class="skill-row__aside"
            title="Worth of the attached status effects"
          >+{{ round(skill.coefficients.effect_value) }}</span></td>
          <td class="col-num">×{{ skill.coefficients.effective_targets }}</td>
          <td class="col-num">{{ skill.min_range }}–{{ skill.max_range }}</td>
          <td class="col-num">
            {{ skill.cost.action_points }}<span
              v-if="skill.coefficients.approach_ap"
              class="skill-row__aside"
              title="Action points spent walking into range, spread over a fight"
            >+{{ round(skill.coefficients.approach_ap) }}</span>
          </td>
          <td class="col-num">{{ skill.cost.energy }}</td>
          <td class="col-num">{{ round(skill.casts_per_turn) }}</td>
          <td class="col-num is-key">{{ round(skill.power_per_ap) }}</td>
          <td class="col-num">{{ round(skill.power_per_turn) }}</td>
        </tr>

        <tr v-if="expanded === skill.skill_id" class="skill-detail">
          <td colspan="10">
            <p v-if="skill.description" class="skill-detail__text">{{ skill.description }}</p>

            <dl class="skill-detail__grid">
              <div><dt>Violation</dt><dd>{{ skill.violation ?? '—' }}<span v-if="skill.domain" class="domain">{{ skill.domain }}</span></dd></div>
              <div><dt>Cells hit</dt><dd>{{ skill.coefficients.cells }} × {{ skill.coefficients.charges }} charge{{ skill.coefficients.charges === 1 ? '' : 's' }}</dd></div>
              <div><dt>Area factor</dt><dd>×{{ skill.coefficients.area_factor }}</dd></div>
              <div><dt>Charge factor</dt><dd>×{{ skill.coefficients.charge_factor }}</dd></div>
              <div><dt>Health cost</dt><dd>{{ skill.cost.health }}</dd></div>
              <div><dt>Sustain</dt><dd>{{ skill.sustain_turns ? `${skill.sustain_turns} turns` : 'unlimited' }}</dd></div>
              <div><dt>vs one body</dt><dd>{{ round(skill.power_single_target) }}</dd></div>
              <div><dt>Whole pattern</dt><dd>{{ round(skill.power) }}</dd></div>
              <div><dt>Power / energy</dt><dd>{{ round(skill.power_per_energy) }}</dd></div>
            </dl>

            <p class="skill-detail__formula">
              ({{ round(skill.coefficients.magnitude) }} impact
              + {{ round(skill.coefficients.effect_value) }} effects)
              × {{ skill.coefficients.area_factor }} area
              × {{ skill.coefficients.charge_factor }} charges
              = {{ round(skill.power) }} power,
              over {{ skill.coefficients.effective_ap }} action points
              = <strong>{{ round(skill.power_per_ap) }} per AP</strong>
            </p>

            <ul v-if="offCurve(skill)" class="skill-detail__curve">
              <li v-for="finding in skill.curve.findings" :key="finding">{{ finding }}</li>
              <li v-if="skill.curve.base">
                Curve target {{ skill.curve.base }} for its grade; authored at
                {{ skill.curve.deviation }}× that.
              </li>
            </ul>
          </td>
        </tr>
      </template>
    </tbody>

    <tbody v-if="!groups.length">
      <tr>
        <td colspan="10" class="skill-table__empty">No school is open at this grade.</td>
      </tr>
    </tbody>
  </table>
</template>

<script setup>
import { ref } from 'vue';

defineProps({
  groups: { type: Array, required: true },
});

const expanded = ref(null);

// One glyph per skill type so a long table can be scanned by shape rather than
// read word by word.
const TYPE_GLYPHS = {
  attack: '⚔',
  defense: '🛡',
  heal: '✚',
  buff: '▲',
  debuff: '▼',
  summon: '☗',
  utility: '⚙',
  special: '★',
};

function typeGlyph(type) {
  return TYPE_GLYPHS[type] ?? '◆';
}

function toggle(id) {
  expanded.value = expanded.value === id ? null : id;
}

function offCurve(skill) {
  return Boolean(skill.curve?.findings?.length);
}

function round(value) {
  if (value === null || value === undefined) return '—';
  return Math.round(value * 10) / 10;
}
</script>

<style scoped>
.skill-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
}

.skill-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 0.25rem 0.35rem;
  font-size: 0.55rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--gm-text-dim);
  text-align: right;
  white-space: nowrap;
  background: var(--gm-surface-0);
  border-bottom: 1px solid var(--gm-border);
}

.skill-table thead th.col-name,
.skill-table thead th.col-type {
  text-align: left;
}

.skill-school th {
  position: sticky;
  top: 1.35rem;
  z-index: 1;
  display: table-cell;
  padding: 0.3rem 0.35rem 0.15rem;
  text-align: left;
  background: var(--gm-surface-0);
  border-bottom: 1px solid var(--gm-border-dim);
}

.skill-school__name {
  font-family: var(--gm-font-display);
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--gm-accent);
}

.skill-school__count,
.skill-school__best {
  margin-left: var(--gm-gap-sm);
  font-size: 0.58rem;
  font-weight: 400;
  color: var(--gm-text-dim);
}

.skill-table td {
  padding: 0.22rem 0.35rem;
  border-top: 1px solid var(--gm-border-dim);
  color: var(--gm-text-muted);
}

.col-num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  width: 4.5rem;
}

.col-type {
  width: 6rem;
}

.is-key {
  color: var(--gm-text);
  font-weight: 700;
}

.skill-row {
  cursor: pointer;
}

.skill-row:hover,
.skill-row.is-open {
  background: var(--gm-accent-softer);
}

.skill-row.is-off-curve .skill-row__name {
  text-decoration: underline dotted var(--gm-accent-hi);
}

.skill-row__type {
  font-size: 0.62rem;
  text-transform: capitalize;
  color: var(--gm-text-dim);
  white-space: nowrap;
}

.skill-row__type.type-attack { color: var(--gm-danger); }
.skill-row__type.type-defense { color: var(--gm-info); }
.skill-row__type.type-heal { color: var(--gm-success); }
.skill-row__type.type-buff { color: var(--gm-accent-hi); }
.skill-row__type.type-debuff { color: #a888c9; }

.skill-row__name {
  color: var(--gm-text);
}

.skill-row__grade {
  margin-left: 0.3rem;
  font-size: 0.58rem;
  color: var(--gm-text-dim);
}

.skill-row__flag {
  margin-left: 0.3rem;
  font-weight: 700;
  color: var(--gm-accent-hi);
}

.skill-row__aside {
  color: var(--gm-text-dim);
  font-size: 0.62rem;
  margin-left: 0.1rem;
}

.skill-detail td {
  background: var(--gm-surface-sunken);
  padding: var(--gm-gap-sm);
}

.skill-detail__text {
  margin: 0 0 var(--gm-gap-sm);
  font-size: 0.75rem;
  color: var(--gm-text-muted);
  font-style: italic;
}

.skill-detail__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(9rem, 1fr));
  gap: 0.3rem var(--gm-gap);
  margin: 0 0 var(--gm-gap-sm);
}

.skill-detail__grid dt {
  font-size: 0.58rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--gm-text-dim);
}

.skill-detail__grid dd {
  margin: 0;
  font-size: 0.76rem;
  color: var(--gm-text);
}

.domain {
  margin-left: 0.3rem;
  font-size: 0.58rem;
  text-transform: uppercase;
  color: var(--gm-text-dim);
}

.skill-detail__formula {
  margin: 0;
  padding: var(--gm-gap-sm);
  font-size: 0.72rem;
  line-height: 1.5;
  color: var(--gm-text-muted);
  background: var(--gm-surface-1);
  border-left: 2px solid var(--gm-accent);
  border-radius: var(--gm-radius-sm);
}

.skill-detail__curve {
  margin: var(--gm-gap-sm) 0 0;
  padding-left: 1rem;
  font-size: 0.72rem;
  color: var(--gm-accent-hi);
}

.skill-table__empty {
  padding: var(--gm-gap);
  font-size: 0.8rem;
  font-style: italic;
  color: var(--gm-text-dim);
}
</style>
